(() => {
  'use strict';
  if (window.__GREMARI_COMPARE_PAGE_V1__) return;
  window.__GREMARI_COMPARE_PAGE_V1__ = true;

  const waitForApi = async () => {
    for (let i = 0; i < 80; i += 1) {
      if (window.GremariCompare) return window.GremariCompare;
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    return null;
  };

  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;').replaceAll("'", '&#039;');

  const text = (value, depth = 0) => {
    if (value === null || value === undefined || value === '') return '—';
    if (depth > 4) return '—';

    if (Array.isArray(value)) {
      const values = value
        .map((entry) => text(entry, depth + 1))
        .filter((entry) => entry && entry !== '—' && entry !== '[object Object]');
      return [...new Set(values)].join(', ') || '—';
    }

    if (typeof value === 'object') {
      const preferredKeys = [
        'label', 'name', 'title', 'display_name', 'displayName',
        'value', 'text', 'colour', 'color', 'hex'
      ];

      for (const key of preferredKeys) {
        if (value[key] !== undefined && value[key] !== null && value[key] !== '') {
          const resolved = text(value[key], depth + 1);
          if (resolved && resolved !== '—' && resolved !== '[object Object]') return resolved;
        }
      }

      if (value.reference) {
        const resolved = text(value.reference, depth + 1);
        if (resolved && resolved !== '—') return resolved;
      }

      if (value.swatch) {
        const resolved = text(value.swatch, depth + 1);
        if (resolved && resolved !== '—') return resolved;
      }

      return '—';
    }

    const stringValue = String(value);
    return stringValue === '[object Object]' ? '—' : stringValue;
  };

  const money = (cents, currency = 'EUR') => {
    const value = Number(cents);
    if (!Number.isFinite(value)) return '—';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value / 100);
    } catch (_) {
      return `${(value / 100).toFixed(2)} ${currency}`;
    }
  };

  const unwrapMetafield = (entry) => {
    if (entry === null || entry === undefined) return null;
    if (typeof entry === 'object' && Object.prototype.hasOwnProperty.call(entry, 'value')) return entry.value;
    return entry;
  };

  const getValue = (product, field) => {
    const namespace = String(field?.namespace || 'custom').trim();
    const key = String(field?.key || '').trim();
    if (!namespace || !key) return null;
    return unwrapMetafield(product?.metafields?.[`${namespace}.${key}`]);
  };

  const valueHtml = (product, field) => {
    const output = text(getValue(product, field));
    if (/^https?:\/\//i.test(output)) {
      return `<a href="${escapeHtml(output)}" target="_blank" rel="noopener">↗</a>`;
    }
    return escapeHtml(output);
  };

  function fields(root) {
    try {
      const parsed = JSON.parse(root.querySelector('[data-gmc-fields]')?.textContent || '[]');
      return Array.isArray(parsed) ? parsed.filter((x) => x?.label && x?.namespace && x?.key) : [];
    } catch (_) {
      return [];
    }
  }

  function renderEmpty(root) {
    const mount = root.querySelector('[data-gmc-page-content]');
    if (!mount) return;
    mount.innerHTML = `
      <div class="gmc-page__empty">
        <h2>${escapeHtml(root.dataset.emptyHeading || 'No products to compare')}</h2>
        <p>${escapeHtml(root.dataset.emptyText || '')}</p>
        <a href="${escapeHtml(root.dataset.productsUrl || '/collections/all')}">${escapeHtml(root.dataset.productsLabel || 'Shop products')}</a>
      </div>`;
  }

  function renderTable(root, products, comparisonFields) {
    const mount = root.querySelector('[data-gmc-page-content]');
    if (!mount) return;

    const availableLabel = root.dataset.availableLabel || 'Available';
    const unavailableLabel = root.dataset.unavailableLabel || 'Unavailable';

    const heads = products.map((p, index) => {
      const image = p.image
        ? `<img class="gmc-product-head__image" src="${escapeHtml(p.image)}" alt="${escapeHtml(p.title || '')}" loading="lazy">`
        : `<div class="gmc-product-head__image gmc-product-head__image--empty"></div>`;

      const cartAction = p.requires_selection
        ? `<a class="gmc-addcart-btn" href="${escapeHtml(p.url || `/products/${p.handle}`)}">${escapeHtml(root.dataset.chooseOptionsLabel || 'Choose options')}</a>`
        : `<button type="button" class="gmc-addcart-btn" data-gmc-addcart="${escapeHtml(String(p.variant_id || ''))}"
             ${(!p.variant_id || p.variant_available === false) ? 'disabled' : ''}>
             ${escapeHtml(p.variant_available === false ? unavailableLabel : (root.dataset.addCartLabel || 'Add to bag'))}
           </button>`;

      return `
        <th class="gmc-table__product-head">
          <div class="gmc-product-head">
            <button type="button" class="gmc-product-head__remove" data-gmc-remove="${escapeHtml(p.handle)}" aria-label="${escapeHtml(root.dataset.removeLabel || 'Remove')}">×</button>
            ${image}
            <a class="gmc-product-head__title" href="${escapeHtml(p.url || `/products/${p.handle}`)}">${escapeHtml(p.title || p.handle)}</a>
            <div class="gmc-product-head__price">${escapeHtml(money(p.price, p.currency || 'EUR'))}</div>
            <div class="gmc-product-head__actions">
              <button type="button" class="gmc-mini-btn" data-gmc-move="${escapeHtml(p.handle)}" data-direction="-1" ${index === 0 ? 'disabled' : ''}>←</button>
              <button type="button" class="gmc-mini-btn" data-gmc-move="${escapeHtml(p.handle)}" data-direction="1" ${index === products.length - 1 ? 'disabled' : ''}>→</button>
              ${cartAction}
            </div>
          </div>
        </th>`;
    }).join('');

    const rows = comparisonFields.map((field) => `
      <tr>
        <th class="gmc-table__label" scope="row">${escapeHtml(field.label)}</th>
        ${products.map((p) => `<td class="gmc-table__value">${valueHtml(p, field)}</td>`).join('')}
      </tr>`).join('');

    mount.innerHTML = `
      <div class="gmc-table-wrap">
        <table class="gmc-table">
          <thead><tr><th class="gmc-table__label" scope="col">${escapeHtml(root.dataset.productLabel || 'Product')}</th>${heads}</tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;
  }

  async function addToCart(variantId) {
    const route = window.GremariCommerceTools?.routes?.cartAdd || '/cart/add.js';
    const response = await fetch(route.endsWith('.js') ? route : `${route}.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: Number(variantId), quantity: 1 })
    });
    if (!response.ok) throw new Error('Cart add failed');
    return response.json();
  }

  async function init(root) {
    const api = await waitForApi();
    if (!api) return;

    const comparisonFields = fields(root);
    const clear = root.querySelector('[data-gmc-clear]');

    const refresh = async () => {
      const items = api.getItems();
      if (clear) clear.hidden = items.length === 0;
      if (!items.length) return renderEmpty(root);

      const mount = root.querySelector('[data-gmc-page-content]');
      if (mount) mount.innerHTML = `<div class="gmc-loading">${escapeHtml(window.GremariCommerceTools?.labels?.loading || 'Loading…')}</div>`;

      const products = await Promise.all(items.map(async (item) => {
        try {
          return { ...(await api.fetchProductData(item.handle)), handle: item.handle };
        } catch (_) {
          return { ...(item.data || {}), handle: item.handle, title: item.title, url: item.url, image: item.image };
        }
      }));
      renderTable(root, products, comparisonFields);
    };

    root.addEventListener('click', async (event) => {
      const remove = event.target.closest('[data-gmc-remove]');
      if (remove) {
        api.remove(remove.dataset.gmcRemove);
        return refresh();
      }

      const move = event.target.closest('[data-gmc-move]');
      if (move) {
        api.move(move.dataset.gmcMove, Number(move.dataset.direction || 0));
        return refresh();
      }

      const add = event.target.closest('[data-gmc-addcart]');
      if (add) {
        const id = add.dataset.gmcAddcart;
        if (!id) return;
        const original = add.textContent;
        add.disabled = true;
        add.textContent = root.dataset.addingLabel || 'Adding…';
        try {
          await addToCart(id);
          add.textContent = root.dataset.addedLabel || 'Added';
          api.refreshCart?.();
        } catch (_) {
          add.textContent = root.dataset.errorLabel || 'Try again';
        } finally {
          setTimeout(() => {
            add.textContent = original;
            add.disabled = false;
          }, 1200);
        }
      }
    });

    clear?.addEventListener('click', () => {
      api.clear();
      refresh();
    });

    window.addEventListener('gremari:compare:changed', refresh);
    await refresh();
  }

  const boot = () => document.querySelectorAll('[data-gmc-page]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
